import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Delete,
  PersonAdd,
  ContentCopy,
  CheckCircle,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/AuthContext';
import { invitationsService } from '../../services/firebase/invitations.service';
import { StaffInvitationFormData, UserRole } from '../../types/organization.types';

const invitationSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'manager', 'viewer'] as const),
});

export default function TeamManagement() {
  const { user } = useAuth();
  const { organization, members, canManageTeam } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<StaffInvitationFormData>({
    resolver: zodResolver(invitationSchema),
    defaultValues: {
      role: 'viewer',
    },
  });

  const handleInvite = async (data: StaffInvitationFormData) => {
    if (!user?.organizationId || !organization) return;

    try {
      setLoading(true);
      const result = await invitationsService.createStaffInvitation(
        user.organizationId,
        data.email,
        data.role,
        user.uid,
        {
          organizationName: organization.name,
          inviterName: user.displayName || 'A team member',
        }
      );

      setInvitationLink(result.invitationLink);
      setInviteDialogOpen(false);
      setSuccessDialogOpen(true);
      reset();
      enqueueSnackbar('Invitation link created!', { variant: 'success' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create invitation';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setLinkCopied(true);
      enqueueSnackbar('Link copied to clipboard!', { variant: 'success' });
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setInvitationLink('');
    setLinkCopied(false);
  };

  const getRoleColor = (role?: UserRole) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'manager':
        return 'info';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  if (!canManageTeam) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Team Management</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          You don't have permission to manage team members.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Team Members</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your organization's team members and invitations
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>{member.displayName}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Chip label={member.role.toUpperCase()} color={getRoleColor(member.role)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    {member.role !== 'owner' && member.userId !== user?.uid && (
                      <IconButton size="small" color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleInvite)}>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogContent>
            <TextField
              {...register('email')}
              label="Email Address"
              type="email"
              fullWidth
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={loading}
            />
            <TextField
              {...register('role')}
              label="Role"
              select
              fullWidth
              margin="normal"
              error={!!errors.role}
              helperText={errors.role?.message || 'Select the role for this team member'}
              disabled={loading}
              defaultValue="viewer"
            >
              <MenuItem value="admin">Admin - Full management access</MenuItem>
              <MenuItem value="manager">Manager - Can manage daily operations</MenuItem>
              <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
            </TextField>
            <Alert severity="info" sx={{ mt: 2 }}>
              You'll receive a link to share with the invitee after creating the invitation.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Creating...' : 'Create Invitation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Success Dialog with Invitation Link */}
      <Dialog open={successDialogOpen} onClose={handleCloseSuccessDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Invitation Link
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this link with the person you want to invite:
          </Typography>
          <TextField
            fullWidth
            value={invitationLink}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyLink} edge="end">
                    {linkCopied ? <CheckCircle color="success" /> : <ContentCopy />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Alert severity="info">
            This invitation link expires in 7 days.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyLink} startIcon={linkCopied ? <CheckCircle /> : <ContentCopy />}>
            {linkCopied ? 'Copied!' : 'Copy Link'}
          </Button>
          <Button variant="contained" onClick={handleCloseSuccessDialog}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
